import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UploadCloud, LayoutGrid, AlertTriangle, History, Store, Gauge, Users, Filter, ChevronDown, ChevronUp, X, FileSpreadsheet, Database, Boxes, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { Header } from "@/components/recon/Header";
import { Button } from "@/components/ui/button";
import { KpiStrip } from "@/components/recon/KpiStrip";
import { UploadPanel } from "@/components/recon/UploadPanel";
import { FiltersBar } from "@/components/recon/FiltersBar";
import { ResultsTable } from "@/components/recon/ResultsTable";
import { OrderDetailDrawer } from "@/components/recon/OrderDetailDrawer";
import { HistoryTable } from "@/components/recon/HistoryTable";
import { StoresDirectory } from "@/components/recon/StoresDirectory";
import { PerformancePanel } from "@/components/recon/PerformancePanel";
import { EmployeesPanel } from "@/components/recon/EmployeesPanel";
import { InventoryPanel } from "@/components/recon/InventoryPanel";
import { CalendarPanel } from "@/components/recon/CalendarPanel";
import { StatusLegend } from "@/components/recon/StatusLegend";

const TABS = [
  { id: "upload", label: "Cargar", icon: UploadCloud, color: "#2563EB" },
  { id: "main", label: "Vista Principal", icon: LayoutGrid, color: "#4F46E5" },
  { id: "calendar", label: "Calendario", icon: CalendarDays, color: "#2563EB" },
  { id: "exceptions", label: "Excepciones", icon: AlertTriangle, color: "#DC2626" },
  { id: "performance", label: "Desempeño", icon: Gauge, color: "#059669" },
  { id: "inventory", label: "Inventario", icon: Boxes, color: "#EA580C" },
  { id: "history", label: "Historial", icon: History, color: "#7C3AED" },
  { id: "stores", label: "Tiendas", icon: Store, color: "#0D9488" },
  { id: "employees", label: "Empleados", icon: Users, color: "#DB2777" },
];

export default function Dashboard() {
  const qc = useQueryClient();
  const [runId, setRunId] = useState(null);
  const [runInit, setRunInit] = useState(false);
  const [tab, setTab] = useState("upload");
  const [filters, setFilters] = useState({});
  const [slaDays, setSlaDays] = useState(7);
  const [drawerResult, setDrawerResult] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Load latest completed run on mount
  const { data: latest } = useQuery({
    queryKey: ["runs", "latest"],
    queryFn: async () => (await api.get("/recon/runs/latest")).data,
  });
  useEffect(() => {
    if (!runInit && latest) {
      setRunInit(true);
      if (latest.id) { setRunId(latest.id); setTab("main"); }
    }
  }, [latest, runInit]);

  const { data: run } = useQuery({
    queryKey: ["run", runId],
    queryFn: async () => (await api.get(`/recon/runs/${runId}`)).data,
    enabled: !!runId,
  });

  const { data: options } = useQuery({
    queryKey: ["filter-options", runId],
    queryFn: async () => (await api.get(`/recon/runs/${runId}/filter-options`)).data,
    enabled: !!runId,
  });

  const { data: analytics } = useQuery({
    queryKey: ["analytics", runId, slaDays, "store"],
    queryFn: async () => (await api.get(`/recon/runs/${runId}/analytics`, { params: { sla_days: slaDays, group_by: "store" } })).data,
    enabled: !!runId,
  });

  const { data: cvData } = useQuery({
    queryKey: ["current-view"],
    queryFn: async () => (await api.get("/recon/current-view")).data,
  });
  const cvMeta = cvData?.meta;

  const summary = run?.summary;
  const slaSummary = analytics?.sla_summary;

  const onUploadCompleted = (newRunId) => {
    setRunId(newRunId);
    setFilters({});
    qc.invalidateQueries({ queryKey: ["runs"] });
    qc.invalidateQueries({ queryKey: ["run", newRunId] });
  };

  const onSelectRun = (id) => {
    setRunId(id);
    setFilters({});
    setTab("main");
  };

  const onPickStatus = (code) => {
    setFilters((f) => ({ ...f, status: code || "", sla: "" }));
    setTab("main");
  };

  const onPickSla = (code) => {
    setFilters((f) => ({ ...f, sla: code || "", status: "" }));
    setTab("main");
  };

  const openDrawer = (result) => { setDrawerResult(result); setDrawerOpen(true); };

  const showFilters = (tab === "main" || tab === "exceptions") && runId;
  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const showKpis = runId && (tab === "main" || tab === "exceptions" || tab === "history");

  const rangeLabel = useMemo(() => {
    const dr = run?.date_range;
    if (!dr?.from) return null;
    return `${dr.from} → ${dr.to}`;
  }, [run]);

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="dashboard">
      <Header runId={runId} />

      {/* Tabs */}
      <div className="bg-white border-b border-[#E2E8F0] px-4 sm:px-6 sticky top-14 z-20">
        <div className="flex items-center gap-1 overflow-x-auto thin-scroll">
          {TABS.map((t) => {
            const active = tab === t.id;
            const Icon = t.icon;
            const style = {
              borderColor: active ? t.color : "transparent",
              fontWeight: active ? 600 : 500,
            };
            if (active) {
              style.color = t.color;
              style.backgroundColor = `${t.color}14`;
            }
            return (
              <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
                style={style}
                className={`flex items-center gap-2 px-3 py-3 text-sm whitespace-nowrap border-b-2 -mb-px rounded-t-sm transition-colors
                  ${active ? "" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"}`}>
                <Icon size={15} style={{ color: t.color }} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {showFilters && (
        <div className="bg-white border-b border-[#E2E8F0] px-4 sm:px-6 py-2 flex items-center gap-2" data-testid="filters-toolbar">
          <Button variant="outline" size="sm" data-testid="toggle-filters-button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="h-8 rounded-sm gap-1.5 text-slate-600 border-slate-300">
            <Filter size={14} /> Filtros
            {activeFilterCount > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-[#2563EB] text-white text-[10px] font-semibold">{activeFilterCount}</span>
            )}
            {filtersOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </Button>
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" data-testid="toolbar-clear-filters"
              onClick={() => setFilters({})} className="h-8 rounded-sm text-slate-500 gap-1 text-xs">
              <X size={13} /> Limpiar
            </Button>
          )}
        </div>
      )}
      {showFilters && filtersOpen && (
        <FiltersBar options={options} filters={filters} setFilters={setFilters}
          showStatus={tab === "main"} slaDays={slaDays} setSlaDays={setSlaDays} />
      )}

      <main className="px-4 sm:px-6 py-5 space-y-5">
        {showKpis && (
          <div>
            {rangeLabel && (
              <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h1 className="font-heading text-lg font-bold tracking-tight text-slate-900">Resumen ejecutivo</h1>
                  <span className="inline-flex items-center gap-1.5 rounded-sm bg-[#0F172A] text-white px-2.5 py-1 text-xs font-semibold" data-testid="run-report-name">
                    <FileSpreadsheet size={13} /> Reporte: {run?.file_name}
                  </span>
                  {cvMeta?.file_name && (
                    <span className="inline-flex items-center gap-1.5 rounded-sm bg-[#ECFDF5] text-[#065F46] border border-[#34D399] px-2.5 py-1 text-xs font-medium" data-testid="run-current-view">
                      <Database size={13} /> Vista: {cvMeta.file_name}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400 font-mono" data-testid="run-range">{rangeLabel}</span>
                  <StatusLegend />
                </div>
              </div>
            )}
            <KpiStrip summary={summary} slaSummary={slaSummary} activeStatus={filters.status}
              activeSla={filters.sla} onPickStatus={onPickStatus} onPickSla={onPickSla} />
          </div>
        )}

        {tab === "upload" && <UploadPanel onCompleted={onUploadCompleted} onViewResults={() => setTab("main")} />}

        {tab === "main" && (
          runId ? (
            <ResultsTable runId={runId} mode="main" filters={filters} onRowClick={openDrawer} slaDays={slaDays} />
          ) : <EmptyRun onUpload={() => setTab("upload")} />
        )}

        {tab === "exceptions" && (
          runId ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">
                Casos que requieren acción: diferencias, pendientes en STORIS, solo en UI y datos incompletos.
              </p>
              <ResultsTable runId={runId} mode="exceptions" filters={filters} onRowClick={openDrawer} slaDays={slaDays} />
            </div>
          ) : <EmptyRun onUpload={() => setTab("upload")} />
        )}

        {tab === "performance" && (
          runId ? (
            <PerformancePanel runId={runId} slaDays={slaDays} setSlaDays={setSlaDays} />
          ) : <EmptyRun onUpload={() => setTab("upload")} />
        )}

        {tab === "history" && (
          <HistoryTable onSelectRun={onSelectRun} currentRunId={runId} />
        )}

        {tab === "stores" && <StoresDirectory />}

        {tab === "inventory" && <InventoryPanel />}

        {tab === "calendar" && <CalendarPanel />}

        {tab === "employees" && <EmployeesPanel />}
      </main>

      <OrderDetailDrawer result={drawerResult} open={drawerOpen} onOpenChange={setDrawerOpen} runId={runId} />
    </div>
  );
}

function EmptyRun({ onUpload }) {
  return (
    <div className="bg-white border border-dashed border-slate-300 rounded-sm py-16 flex flex-col items-center text-center" data-testid="empty-run">
      <div className="h-12 w-12 rounded-sm bg-[#0F172A] text-white flex items-center justify-center">
        <UploadCloud size={24} />
      </div>
      <p className="mt-4 text-slate-700 font-medium">Aún no hay una conciliación activa</p>
      <p className="text-sm text-slate-400 mt-1">Sube un reporte STORIS para empezar.</p>
      <button onClick={onUpload} data-testid="empty-upload-cta"
        className="mt-4 text-sm text-[#2563EB] font-medium hover:underline">Ir a cargar archivo →</button>
    </div>
  );
}
